import Layout from '@/layouts/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Link, router } from '@inertiajs/react';
import { CalendarDays, Sparkles, Zap } from 'lucide-react';
import { useCallback } from 'react';
import { GaugePill } from '@/components/journal/GaugePill';
import { PatientPageHeader } from '@/components/patient';
import { breadcrumbs } from '@/components/ui/breadcrumb';
import { CalendarBadge } from '@/components/ui/calendar-badge';
import { getCalendarBadgeParts } from '@/lib/date';

type PatientSummary = {
  id: number;
  display_name: string;
  demo?: boolean;
};

type JournalListEntry = {
  id: number;
  date: string;
  status: string;
  title: string;
  highlight?: string | null;
  emotional_label?: string | null;
  emotion_intensity?: number | null;
  mood_level?: number | null;
  energy_level?: number | null;
  completed_at?: string | null;
  summary_available: boolean;
  safety_blocked?: boolean;
};

type Pagination = {
  current_page: number;
  total_pages: number;
  total_count: number;
  next_page?: number | null;
  prev_page?: number | null;
};

export type TherapistJournalIndexProps = {
  patient: PatientSummary;
  entries: JournalListEntry[];
  pagination: Pagination;
  hideChrome?: boolean;
};

function formatDate(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric' });
}

function getCalendarBadgeLabel(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function TherapistJournalIndex({
  patient,
  entries,
  pagination,
  hideChrome = false,
}: TherapistJournalIndexProps) {
  const goToPage = useCallback(
    (page: number) => {
      if (page === pagination.current_page) return;
      router.get(`/therapists/patients/${patient.id}/journals`, { page }, { preserveScroll: true });
    },
    [pagination.current_page, patient.id],
  );

  const content = (
    <div className="w-full space-y-8">
      {!hideChrome &&
        breadcrumbs([
          { text: 'Clienti', url: '/therapists/patients' },
          {
            text: patient.display_name,
            url: `/therapists/patients/${patient.id}`,
          },
          {
            text: 'Diari',
            current: true,
          },
        ])}
      {!hideChrome && <PatientPageHeader patient={patient} active="journals" />}

      <div className="space-y-3">
        {entries.length === 0 ? (
          <Card className="p-8">
            <EmptyState
              title="Nessun diario completato ancora disponibile."
              description="Quando il cliente approva un diario completato, troverai qui il relativo Summary 5F."
              icon={<Sparkles className="h-8 w-8" aria-hidden />}
            />
          </Card>
        ) : (
          <div className="space-y-4">
            {entries.map((entry) => {
              const badge = getCalendarBadgeParts(entry.date);
              const badgeLabel = getCalendarBadgeLabel(entry.date);
              return (
                <Card key={entry.id} className="p-5 bg-background/80 border-muted/70">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-start">
                      {badge ? (
                        <div className="flex items-center gap-2">
                          <CalendarBadge day={badge.day} month={badge.month} />
                          <span className="sr-only">{badgeLabel}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CalendarDays className="w-4 h-4" />
                          <span>{formatDate(entry.date)}</span>
                        </div>
                      )}
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-lg font-semibold leading-snug">{entry.title}</h3>
                          {entry.safety_blocked && (
                            <Badge variant="destructive">Bloccato per sicurezza</Badge>
                          )}
                        </div>
                        {entry.highlight && (
                          <p className="text-sm text-muted-foreground max-w-2xl">
                            {entry.highlight}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-start gap-2 lg:items-end">
                      {typeof entry.mood_level === 'number' && (
                        <GaugePill
                          label="Umore"
                          value={entry.mood_level}
                          icon={<Sparkles className="w-3 h-3" />}
                        />
                      )}
                      {typeof entry.energy_level === 'number' && (
                        <GaugePill
                          label="Energia"
                          value={entry.energy_level}
                          icon={<Zap className="w-3 h-3" />}
                        />
                      )}
                      <Button asChild size="sm" variant="secondary">
                        <Link href={`/therapists/patients/${patient.id}/journals/${entry.id}`}>
                          Vedi dettaglio
                        </Link>
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {pagination.total_pages > 1 && (
        <div className="flex justify-center gap-2 pt-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={pagination.current_page === 1}
            onClick={() => goToPage(pagination.current_page - 1)}
          >
            &laquo;
          </Button>
          {Array.from({ length: pagination.total_pages }, (_, index) => index + 1).map((page) => (
            <Button
              key={page}
              size="sm"
              variant={page === pagination.current_page ? 'default' : 'ghost'}
              onClick={() => goToPage(page)}
              disabled={page === pagination.current_page}
            >
              {page}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            disabled={pagination.current_page === pagination.total_pages}
            onClick={() => goToPage(pagination.current_page + 1)}
          >
            &raquo;
          </Button>
        </div>
      )}
    </div>
  );

  if (hideChrome) return content;

  return (
    <Layout isAdmin user={{ name: 'Psicologo' }}>
      {content}
    </Layout>
  );
}
